import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { of, timer } from 'rxjs';
import { catchError, debounce, filter, map, switchMap } from 'rxjs/operators';
import { BrandsService } from '@emi/shared/data-access';
import * as brandActions from './brand-actions';
import { HttpErrorResponse } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { BrandState } from './brand-reducer';
import { getBrandState } from './brand-selectors';
import { Filters, BrandDto } from '@emi/shared/models';
import { DataExtractionState } from '../extraction/extraction-reducer';
import { getSelectedFilters } from '../extraction/extraction-selectors';
import { createFilterDto } from '@emi/shared/utils';
import * as dataExtractionActions from '../extraction/extraction-actions';
import { selectSkuSearch, SkuSearchState } from '@emi/shared/state/sku-search';

@Injectable()
export class BrandEffects {
    private currentState: BrandState;
    private currentFilters: Filters;
    private previousFilters: Filters;
    private searchTermState: string;
    private previousSearchTermState: string;
    private includeSearchPhrase: boolean | undefined;

    constructor(
        private actions$: Actions,
        private brandsService: BrandsService,
        private brandStore: Store<BrandState>,
        private store: Store<DataExtractionState>,
        private skuSearchStore: Store<SkuSearchState>
    ) {
        this.brandStore.pipe(select(getBrandState)).subscribe((brandState) => (this.currentState = brandState));
        this.skuSearchStore.pipe(select(selectSkuSearch)).subscribe((skuSearchState) => (this.searchTermState = skuSearchState.searchTerm, this.includeSearchPhrase = skuSearchState.includeSearchPhrase));
    }

    getBrands$ = createEffect(() =>
        this.actions$.pipe(
            ofType(brandActions.getBrands),
            map(() => {
                return brandActions.isLoadingBrands();
            })
        )
    );

    getBrandsNextPage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(brandActions.getNextPageBrands),
            switchMap(() => {
                return this.brandsService
                    .getBrands(
                        createFilterDto({
                            filterState: this.currentFilters,
                            searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined,
                            pageId: this.currentState.metadata?.nextPageId,
                            searchText: this.currentState.searchText ?? undefined,
                        })
                    )
                    .pipe(
                        map((data: BrandDto) => {
                            this.previousFilters = this.currentFilters;
                            const newPage = data.results;

                            return brandActions.updateBrandsSelectionSuccess({ brands: newPage, metadata: data.metadata });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(brandActions.getBrandsFailure({ error }));
                        })
                    );
            })
        )
    );

    amendSelected$ = createEffect(() =>
        this.actions$.pipe(
            ofType(brandActions.amendBrandSelections),
            switchMap(() => {
                this.store.pipe(select(getSelectedFilters)).subscribe((filtersState) => (this.currentFilters = filtersState));

                return this.brandsService
                    .getBrands(
                        createFilterDto({
                            filterState: this.currentFilters,
                            searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined,
                            pageId: this.currentState.metadata?.nextPageId,
                        })
                    )
                    .pipe(
                        map((brandResponse) => {
                            const amendedBrands = this.currentFilters.brands.filter(
                                (b) => !!brandResponse.results.find((brand) => brand.brandId == b.brandId)
                            );
                            return dataExtractionActions.addBrandsSelection({ brands: amendedBrands });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            if (error.status == 404) {
                                return of(dataExtractionActions.addBrandsSelection({ brands: [] }));
                            }
                            return of(brandActions.getBrandsFailure({ error }));
                        })
                    );
            })
        )
    );

    fetchBrands$ = createEffect(() =>
        this.actions$.pipe(
            ofType(brandActions.fetchBrands),
            switchMap(() => {
                this.store.pipe(select(getSelectedFilters)).subscribe((filtersState) => (this.currentFilters = filtersState));
                if (this.currentFilters.brands.length > 0) {
                    if (this.currentFilters === this.previousFilters && this.searchTermState === this.previousSearchTermState) {
                        return of(
                            brandActions.getBrandsSuccess({
                                brands: this.currentState.brands,
                                metadata: this.currentState.metadata,
                                searchText: this.currentState.searchText,
                            })
                        );
                    }
                }

                return this.brandsService
                    .getBrands(createFilterDto({ filterState: this.currentFilters, searchTerm: this.includeSearchPhrase? this.searchTermState : undefined }))
                    .pipe(
                        map((data: BrandDto) => {
                            this.previousFilters = this.currentFilters;
                            this.previousSearchTermState = this.searchTermState;
                            return brandActions.getBrandsSuccess({
                                brands: data.results,
                                metadata: data.metadata,
                                searchText: this.currentState.searchText,
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(brandActions.getBrandsFailure({ error }));
                        })
                    );
            })
        )
    );

    searchBrands$ = createEffect(() =>
        this.actions$.pipe(
            ofType(brandActions.searchBrands),
            debounce((action) => timer(action.delay)),
            filter((action) => action.searchText !== this.currentState.searchText),
            concatLatestFrom(() => this.store.select(getSelectedFilters)),
            switchMap(([action, currentFilters]) => {
                return this.brandsService
                    .getBrands(
                        createFilterDto({
                            filterState: currentFilters,
                            searchText: action.searchText,
                            searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined,
                        })
                    )
                    .pipe(
                        map((data: BrandDto) => {
                            this.previousFilters = currentFilters;
                            return brandActions.getBrandsSuccess({
                                brands: data.results,
                                metadata: data.metadata,
                                searchText: action.searchText,
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(brandActions.getBrandsFailure({ error }));
                        })
                    );
            })
        )
    );

    setIsLoading$ = createEffect(() =>
        this.actions$.pipe(
            ofType(brandActions.isLoadingBrands),
            map(() => {
                return brandActions.fetchBrands();
            })
        )
    );
}
